import { useState, useEffect, useCallback } from 'react';
import offlineStorage, { calculateSM2Offline } from '../services/offlineStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useOfflineSRS() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOfflineReady, setIsOfflineReady] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSync, setLastSync] = useState(null);

  // Initialize offline storage and service worker
  useEffect(() => {
    const init = async () => {
      try {
        await offlineStorage.init();
        
        // Check for pending reviews
        const pending = await offlineStorage.getPendingReviews();
        setPendingCount(pending.length);

        // Get last sync time
        const stats = await offlineStorage.getStats();
        if (stats?.lastSync) {
          setLastSync(new Date(stats.lastSync));
        }

        setIsOfflineReady(true);
        console.log('[useOfflineSRS] Offline storage ready');
      } catch (error) {
        console.error('[useOfflineSRS] Init error:', error);
      }
    };

    init();

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(reg => console.log('[useOfflineSRS] Service Worker registered'))
        .catch(err => console.error('[useOfflineSRS] SW registration failed:', err));
    }
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('[useOfflineSRS] Back online');
      setIsOnline(true);
      // Trigger sync when back online
      syncPendingReviews();
    };

    const handleOffline = () => {
      console.log('[useOfflineSRS] Gone offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Download cards for offline use
  const downloadCardsForOffline = useCallback(async () => {
    if (!isOnline) {
      console.warn('[useOfflineSRS] Cannot download while offline');
      return false;
    }

    try {
      // Fetch all cards from API (with a high limit)
      const betaCode = localStorage.getItem('betaCode');
      const headers = betaCode ? { 'X-Beta-Code': betaCode } : {};
      const response = await fetch(`${API_URL}/api/srs/session?limit=1000`, { headers });
      if (!response.ok) throw new Error('Failed to fetch cards');

      const data = await response.json();
      const cards = data.cards_due || [];

      // Also fetch stats (with beta_code)
      const statsResponse = await fetch(`${API_URL}/api/srs/stats`, { headers });
      const stats = await statsResponse.json();

      // Save to IndexedDB
      await offlineStorage.saveCards(cards);
      await offlineStorage.saveStats({ ...stats, lastSync: new Date().toISOString() });

      setLastSync(new Date());
      console.log(`[useOfflineSRS] Downloaded ${cards.length} cards for offline use`);

      return true;
    } catch (error) {
      console.error('[useOfflineSRS] Download error:', error);
      return false;
    }
  }, [isOnline]);

  // Get cards (online or offline)
  const getCards = useCallback(async (limit = 20) => {
    if (isOnline) {
      try {
        const betaCode = localStorage.getItem('betaCode');
        const headers = betaCode ? { 'X-Beta-Code': betaCode } : {};
        const response = await fetch(`${API_URL}/api/srs/session?limit=${limit}`, { headers });
        if (response.ok) {
          const data = await response.json();
          // Also save to offline storage for future use
          if (data.cards_due?.length > 0) {
            await offlineStorage.saveCards(data.cards_due);
          }
          return { cards: data.cards_due || [], isOffline: false };
        }
      } catch (error) {
        console.warn('[useOfflineSRS] Online fetch failed, using offline data');
      }
    }

    // Fallback to offline data
    const offlineCards = await offlineStorage.getDueCards(limit);
    return { cards: offlineCards, isOffline: true };
  }, [isOnline]);

  // Get stats (online or offline)
  const getStats = useCallback(async () => {
    if (isOnline) {
      try {
        const betaCode = localStorage.getItem('betaCode');
        const headers = betaCode ? { 'X-Beta-Code': betaCode } : {};
        const response = await fetch(`${API_URL}/api/srs/stats`, { headers });
        if (response.ok) {
          const stats = await response.json();
          // Save for offline use
          await offlineStorage.saveStats({ ...stats, lastSync: new Date().toISOString() });
          return { ...stats, isOffline: false };
        }
      } catch (error) {
        console.warn('[useOfflineSRS] Stats fetch failed, using offline data');
      }
    }

    // Calculate offline stats
    const offlineStats = await offlineStorage.calculateOfflineStats();
    return offlineStats;
  }, [isOnline]);

  // Submit review (online or offline)
  const submitReview = useCallback(async (cardId, quality) => {
    const feedbackMessages = {
      0: "😰 Oublié complètement. Ce mot reviendra bientôt !",
      1: "😕 Difficile... Pratique l'écoute de ce mot.",
      2: "🤔 Presque ! Encore un effort.",
      3: "👍 Correct avec hésitation. Continue !",
      4: "😊 Très bien ! Tu progresses.",
      5: "🎉 Parfait ! Ce mot est bien ancré."
    };

    if (isOnline) {
      try {
        const betaCode = localStorage.getItem('betaCode');
        const headers = { 'Content-Type': 'application/json' };
        if (betaCode) {
          headers['X-Beta-Code'] = betaCode;
        }
        
        const response = await fetch(`${API_URL}/api/srs/review`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ card_id: cardId, quality })
        });

        if (response.ok) {
          const result = await response.json();
          
          // Update local card
          const cards = await offlineStorage.getAllCards();
          const card = cards.find(c => c.id === cardId);
          if (card) {
            card.ease_factor = result.new_ease_factor;
            card.interval = result.new_interval;
            card.next_review = result.next_review;
            card.last_reviewed = new Date().toISOString();
            card.total_reviews = (card.total_reviews || 0) + 1;
            if (quality >= 3) card.correct_reviews = (card.correct_reviews || 0) + 1;
            await offlineStorage.updateCard(card);
          }

          return { ...result, isOffline: false };
        }
      } catch (error) {
        console.warn('[useOfflineSRS] Review failed, saving offline');
      }
    }

    // Offline review
    const cards = await offlineStorage.getAllCards();
    const card = cards.find(c => c.id === cardId);

    if (!card) {
      throw new Error('Card not found');
    }

    // Calculate SM-2 locally
    const { newEaseFactor, newInterval, newRepetitions } = calculateSM2Offline(
      quality,
      card.ease_factor || 2.5,
      card.interval || 1,
      card.repetitions || 0
    );

    // Calculate next review date
    const nextReview = new Date();
    nextReview.setDate(nextReview.getDate() + newInterval);

    // Update card
    card.ease_factor = newEaseFactor;
    card.interval = newInterval;
    card.repetitions = newRepetitions;
    card.next_review = nextReview.toISOString().split('T')[0];
    card.last_reviewed = new Date().toISOString();
    card.total_reviews = (card.total_reviews || 0) + 1;
    if (quality >= 3) {
      card.correct_reviews = (card.correct_reviews || 0) + 1;
    }

    await offlineStorage.updateCard(card);

    // Save pending review for sync
    await offlineStorage.savePendingReview({
      card_id: cardId,
      quality,
      reviewed_at: new Date().toISOString()
    });

    setPendingCount(prev => prev + 1);

    return {
      card_id: cardId,
      thai: card.thai,
      french: card.french,
      new_interval: newInterval,
      new_ease_factor: newEaseFactor,
      next_review: card.next_review,
      feedback: feedbackMessages[quality] || "Continué !",
      isOffline: true
    };
  }, [isOnline]);

  // Sync pending reviews when back online
  const syncPendingReviews = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    console.log('[useOfflineSRS] Starting sync...');

    try {
      const pendingReviews = await offlineStorage.getPendingReviews();

      if (pendingReviews.length === 0) {
        console.log('[useOfflineSRS] No pending reviews to sync');
        setIsSyncing(false);
        return;
      }

      let synced = 0;
      for (const review of pendingReviews) {
        try {
          const betaCode = localStorage.getItem('betaCode');
          const headers = { 'Content-Type': 'application/json' };
          if (betaCode) {
            headers['X-Beta-Code'] = betaCode;
          }
          
          const response = await fetch(`${API_URL}/api/srs/review`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              card_id: review.card_id,
              quality: review.quality
            })
          });

          if (response.ok) {
            synced++;
          }
        } catch (error) {
          console.error('[useOfflineSRS] Failed to sync review:', review.card_id);
        }
      }

      if (synced > 0) {
        await offlineStorage.clearPendingReviews();
        setPendingCount(0);
        console.log(`[useOfflineSRS] Synced ${synced}/${pendingReviews.length} reviews`);
      }

      // Refresh cards from server
      await downloadCardsForOffline();

    } catch (error) {
      console.error('[useOfflineSRS] Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, downloadCardsForOffline]);

  return {
    isOnline,
    isOfflineReady,
    isSyncing,
    pendingCount,
    lastSync,
    downloadCardsForOffline,
    getCards,
    getStats,
    submitReview,
    syncPendingReviews
  };
}

export default useOfflineSRS;
